"use client";

import { c as _c } from "react/compiler-runtime";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FaCheck } from "react-icons/fa6";
import { LuArrowRight, LuHourglass } from "react-icons/lu";
import { ProgressBarLink, useProgressNavigation } from "@/components/progress-bar";
import { useUser } from "@/contexts/user-provider";
import type { SetupChecklistItem } from "@/hooks/use-setup-nag";
import { useSetupNag } from "@/hooks/use-setup-nag";
import { goLiveLabel } from "@/lib/go-live";
import { userIsAdmin } from "@/lib/permissions";
import { noteSetupGuideOpenSource } from "@/lib/setup-checklist-analytics";
import { cx } from "@/lib/tailwind-utils";
import { SidebarSetupChecklist } from "./sidebar-setup-checklist";
import { dismissSetupCompletion } from "@/api/clinic-setup";
import { useSetupChecklist } from "@/api/provider/setup-checklist/setup-checklist-query";
import { invalidateSetupReadiness } from "@/query/clinic-setup-readiness";

/** Before go-live this is a countdown; after it, the same unfinished list is */
function panelHeading(days: number): string {
  return days < 0 ? "Needs attention" : "Onboarding";
}
const URGENT_DAYS = 7;
const BAR_START_MS = 150;
const SEGMENT_FILL_MS = 260;

/** What this clinic still can't do. Capabilities, not wizard steps — the rest
 *  gate nothing. Admin-only. */
export function SetupOnboardingPanel() {
  const $ = _c(48);
  const {
    currentUser
  } = useUser();
  const nag = useSetupNag();
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const [dismissed, setDismissed] = useState(false);
  const isAdmin = userIsAdmin(currentUser);
  const {
    data: setup
  } = useSetupChecklist();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: dismissSetupCompletion,
      onSettled: _temp
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const dismiss = useMutation(t0);
  if (!isAdmin) {
    return null;
  }
  if (!nag || nag.isComplete && dismissed) {
    let t1;
    if ($[1] !== setup) {
      t1 = <SidebarSetupChecklist data={setup} />;
      $[1] = setup;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  if (nag.isComplete) {
    let t1;
    if ($[3] !== dismiss) {
      t1 = () => {
        setDismissed(true);
        dismiss.mutate();
      };
      $[3] = dismiss;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    const finish = t1;
    const t2 = (setup?.totals.open ?? 0) > 0 ? "/settings/setup-guide" : undefined;
    let t3;
    if ($[5] !== finish || $[6] !== nag.totalCount || $[7] !== t2) {
      t3 = <CompletionPanel segments={nag.totalCount} onDismiss={finish} continueHref={t2} />;
      $[5] = finish;
      $[6] = nag.totalCount;
      $[7] = t2;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    return t3;
  }
  const t1 = nag.summary;
  let t2;
  if ($[9] !== nag.daysToGoLive) {
    t2 = panelHeading(nag.daysToGoLive);
    $[9] = nag.daysToGoLive;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== t2) {
    t3 = <span className="shrink-0 font-semibold">{t2}</span>;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const t4 = nag.daysToGoLive <= URGENT_DAYS ? "font-semibold text-destructive" : "text-foreground-secondary";
  let t5;
  if ($[13] !== t4) {
    t5 = cx("flex-1 truncate text-right tabular-nums", t4);
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== nag.daysToGoLive) {
    t6 = goLiveLabel(nag.daysToGoLive);
    $[15] = nag.daysToGoLive;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t5 || $[18] !== t6) {
    t7 = <span className={t5}>{t6}</span>;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== nag.summary || $[21] !== t3 || $[22] !== t7) {
    t8 = <div title={t1} className="flex items-baseline gap-2 pl-0.5 pr-1 text-caption">{t3}{t7}</div>;
    $[20] = nag.summary;
    $[21] = t3;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== nag.items || $[25] !== navigateWithProgress) {
    let t10;
    if ($[27] !== navigateWithProgress) {
      t10 = item => <ChecklistRow key={item.capability} item={item} onClick={item.target ? () => navigateWithProgress(item.target as string) : undefined} />;
      $[27] = navigateWithProgress;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    t9 = nag.items.map(t10);
    $[24] = nag.items;
    $[25] = navigateWithProgress;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[29] !== t9) {
    t10 = <ul className="flex flex-col">{t9}</ul>;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const t11 = `Clinic setup: ${nag.completedCount} of ${nag.totalCount} completed`;
  let t12;
  if ($[31] !== nag.completedCount || $[32] !== nag.totalCount) {
    t12 = <ChecklistBar segments={nag.totalCount} completed={nag.completedCount} />;
    $[31] = nag.completedCount;
    $[32] = nag.totalCount;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== nag.completedCount || $[35] !== nag.totalCount || $[36] !== t11 || $[37] !== t12) {
    t13 = <div className="flex flex-1 gap-1" role="progressbar" aria-valuenow={nag.completedCount} aria-valuemin={0} aria-valuemax={nag.totalCount} aria-label={t11}>{t12}</div>;
    $[34] = nag.completedCount;
    $[35] = nag.totalCount;
    $[36] = t11;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== nag.shortLabel) {
    t14 = <span className="shrink-0 text-caption tabular-nums text-foreground-secondary">{nag.shortLabel}</span>;
    $[39] = nag.shortLabel;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t13 || $[42] !== t14) {
    t15 = <div className="flex items-center gap-2 pl-0.5 pr-1 pt-0.5">{t13}{t14}</div>;
    $[41] = t13;
    $[42] = t14;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== t10 || $[45] !== t15 || $[46] !== t8) {
    t16 = <div className="mb-2 ml-4 mr-2 flex flex-col gap-2 rounded-lg border border-border bg-background p-2.5 text-foreground">{t8}{t10}{t15}</div>;
    $[44] = t10;
    $[45] = t15;
    $[46] = t8;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  return t16;
}
function _temp() {
  return invalidateSetupReadiness();
}
function CompletionPanel(t0) {
  const $ = _c(30);
  const {
    segments,
    onDismiss,
    continueHref
  } = t0;
  const [entered, setEntered] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const frame = requestAnimationFrame(() => setEntered(true));
      return () => cancelAnimationFrame(frame);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const barEndsAt = BAR_START_MS + SEGMENT_FILL_MS * segments;
  const t3 = entered ? 1 : 0;
  const t4 = `${barEndsAt}ms`;
  let t5;
  if ($[2] !== t3 || $[3] !== t4) {
    t5 = {
      opacity: t3,
      transitionDelay: t4
    };
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const actionStyle = t5;
  const t6 = entered ? 1 : 0;
  const t7 = entered ? "translateY(0)" : "translateY(4px)";
  let t8;
  if ($[5] !== t6 || $[6] !== t7) {
    t8 = {
      opacity: t6,
      transform: t7
    };
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = entered ? "scale(1)" : "scale(0.4)";
  let t10;
  if ($[8] !== t9) {
    t10 = <FaCheck role="img" className="h-3.5 w-3.5 shrink-0 text-primary transition-transform duration-300 ease-[cubic-bezier(.34,1.56,.64,1)] motion-reduce:transition-none" style={{
      transform: t9
    }} />;
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="flex-1 truncate text-caption font-medium">You're all set!</span>;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== t10 || $[12] !== t8) {
    t12 = <div className="flex items-center gap-2 transition-all duration-300 ease-out motion-reduce:transition-none" style={t8}>{t10}{t11}</div>;
    $[11] = t10;
    $[12] = t8;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  let t13;
  if ($[14] !== entered || $[15] !== segments) {
    let t14;
    if ($[17] !== entered) {
      t14 = (_, index) => <span key={index} className="h-1 flex-1 origin-left rounded-full bg-primary transition-transform ease-out motion-reduce:transition-none" style={{
        transform: entered ? "scaleX(1)" : "scaleX(0)",
        transitionDuration: `${SEGMENT_FILL_MS}ms`,
        transitionDelay: `${BAR_START_MS + SEGMENT_FILL_MS * index}ms`
      }} />;
      $[17] = entered;
      $[18] = t14;
    } else {
      t14 = $[18];
    }
    t13 = Array.from({
      length: segments
    }, t14);
    $[14] = entered;
    $[15] = segments;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[19] !== segments || $[20] !== t13) {
    t14 = <div className="flex flex-1 gap-1" role="progressbar" aria-valuenow={segments} aria-valuemin={0} aria-valuemax={segments} aria-label="Clinic setup complete">{t13}</div>;
    $[19] = segments;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== actionStyle || $[23] !== continueHref || $[24] !== onDismiss) {
    t15 = continueHref ? <ProgressBarLink href={continueHref} onClick={event => {
      noteSetupGuideOpenSource(event, "onboarding_panel");
      onDismiss();
    }} className="flex w-full items-center justify-center gap-1 rounded-md border border-border py-1.5 text-caption font-medium text-foreground-secondary transition-all duration-200 hover:bg-hover hover:text-foreground motion-reduce:transition-none" style={actionStyle}><span>Continue setup</span><LuArrowRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" /></ProgressBarLink> : <button type="button" onClick={onDismiss} className="flex w-full items-center justify-center gap-1 rounded-md border border-border py-1.5 text-caption font-medium text-foreground-secondary transition-all duration-200 hover:bg-hover hover:text-foreground motion-reduce:transition-none" style={actionStyle}>Dismiss</button>;
    $[22] = actionStyle;
    $[23] = continueHref;
    $[24] = onDismiss;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t12 || $[27] !== t14 || $[28] !== t15) {
    t16 = <div className="mb-2 ml-4 mr-2 flex flex-col gap-2.5 rounded-lg border border-border bg-background p-3 text-foreground">{t12}{t14}{t15}</div>;
    $[26] = t12;
    $[27] = t14;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  return t16;
}

/** Its own component so its mount coincides with the bar's first paint — the */
function ChecklistBar(t0) {
  const $ = _c(12);
  const {
    segments,
    completed
  } = t0;
  const [entered, setEntered] = useState(false);
  const [introDone, setIntroDone] = useState(false);
  let t1;
  let t2;
  if ($[0] !== segments) {
    t1 = () => {
      const frame = requestAnimationFrame(() => setEntered(true));
      const settle = setTimeout(() => setIntroDone(true), BAR_START_MS + SEGMENT_FILL_MS * segments);
      return () => {
        cancelAnimationFrame(frame);
        clearTimeout(settle);
      };
    };
    t2 = [segments];
    $[0] = segments;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== completed || $[4] !== entered || $[5] !== introDone || $[6] !== segments) {
    let t4;
    if ($[8] !== completed || $[9] !== entered || $[10] !== introDone) {
      t4 = (_, index) => <span key={index} className="h-1 flex-1 overflow-hidden rounded-full bg-border"><span className="block h-full w-full origin-left rounded-full bg-primary transition-transform ease-out motion-reduce:transition-none" style={{
          transform: entered && index < completed ? "scaleX(1)" : "scaleX(0)",
          transitionDuration: `${SEGMENT_FILL_MS}ms`,
          transitionDelay: introDone ? "0ms" : `${BAR_START_MS + SEGMENT_FILL_MS * index}ms`
        }} /></span>;
      $[8] = completed;
      $[9] = entered;
      $[10] = introDone;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    t3 = <>{Array.from({
        length: segments
      }, t4)}</>;
    $[3] = completed;
    $[4] = entered;
    $[5] = introDone;
    $[6] = segments;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function ChecklistRow(t0) {
  const $ = _c(16);
  const {
    item,
    onClick
  } = t0;
  const isReady = item.state === "READY";
  let t1;
  if ($[0] !== item.state) {
    t1 = <StatusIcon state={item.state} />;
    $[0] = item.state;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isReady ? "text-foreground-secondary" : "text-foreground";
  let t3;
  if ($[2] !== t2) {
    t3 = cx("flex-1 truncate text-caption", t2);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== item.label || $[5] !== t3) {
    t4 = <span className={t3}>{item.label}</span>;
    $[4] = item.label;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== item.note) {
    t5 = item.note && <span className="shrink-0 text-caption text-foreground-secondary">{item.note}</span>;
    $[7] = item.note;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t4 || $[11] !== t5) {
    t6 = <>{t1}{t4}{t5}</>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const content = t6;
  let t7;
  if ($[13] !== content || $[14] !== onClick) {
    t7 = <li>{onClick ? <button type="button" onClick={onClick} className={cx("flex w-full items-center gap-2 rounded-md py-1 pl-0.5 pr-1 text-left", "hover:bg-hover")}>{content}</button> : <div className="flex w-full items-center gap-2 rounded-md py-1 pl-0.5 pr-1 text-left">{content}</div>}</li>;
    $[13] = content;
    $[14] = onClick;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}

/** One glyph per state in the same quiet ink — sidebar chrome shouldn't compete */
function StatusIcon(t0) {
  const $ = _c(4);
  const {
    state
  } = t0;
  if (state === "READY") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <FaCheck className={cx("h-3.5 w-3.5 shrink-0", "text-primary")} aria-label="Ready" role="img" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (state === "PENDING") {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <LuHourglass className={cx("h-3.5 w-3.5 shrink-0", "text-foreground-secondary")} aria-label="Blocked" role="img" />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (state === "ACTION_NEEDED") {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <LuArrowRight className={cx("h-3.5 w-3.5 shrink-0", "text-foreground-secondary")} aria-label="Action needed" role="img" />;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className={cx("h-3.5 w-3.5 shrink-0", "rounded-full border border-dashed border-border-secondary opacity-60")} aria-label="Not started" role="img" />;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}